import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ButtonBase } from '@material-ui/core';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Inputs, SelectComponet, Tables } from '../../../Components';
import { DateRangePickerComponent } from '../../../Components/Controls/DateRangePickerComponent/DateRangePickerComponent';
import { TableActions } from '../../../Enums';
import { getDownloadableLink, showError } from '../../../Helper';
import { useDebouncedAction } from '../../../Hooks/DebouncedAction';
import {
  AddSmartProposalLog,
  GetGeneratedProposals,
} from '../../../Services/GeneratedProposals';
import PreviewDialog from './components/PreviewDialog';
import ShareDialog from './components/ShareDialog';
import headerData from './headerData';
import { GeneratedProposalTranslationPath } from './i18n/locale';

const GeneratedProposalsView = () => {
  const [filteredData, setFilteredData] = React.useState({
    pageIndex: 1,
    pageSize: 25,
    dateFrom: null,
    dateTo: null,
  });

  const [state, setState] = useState({
    propertyName: '',
    unitRefNumber: '',
    platformType: null,
  });

  const firstFetch = useRef(true);
  const { t } = useTranslation(GeneratedProposalTranslationPath);
  const [data, setData] = useState({ result: [], totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [activeShareType, setActiveShareType] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const getGeneratedProposals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await GetGeneratedProposals(filteredData);

      if (firstFetch.current && !!response.totalCount) {
        firstFetch.current = false;
      }
      setData(response);
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  }, [filteredData]);

  const onActionClicked = (actionEnum, item) => {
    const link = getDownloadableLink(item.proposalFileId);
    if (actionEnum === TableActions.preview.key) {
      setActiveItem(item);
      setIsPreviewDialogOpen(true);
    } else if (actionEnum === TableActions.downloadText.key) {
      const activeUser = JSON.parse(localStorage.getItem('session'));
      AddSmartProposalLog({
        proposalId: activeItem.proposalId,
        proposalFileId: activeItem.proposalFileId,
        proposalFileName: activeItem.propertyName,
        propertyName: activeItem.propertyName,
        unitIds: activeItem.proposalUnitsDetails.map((unit) => +unit.unitId),
        receivers: [
          {
            name: activeUser?.fullName || '',
            email: activeUser?.email || '',
            receiverId: activeUser?.userId || '',
            proposalReceiverType: 2,
          },
        ],
        actionType: 2,
      });
      window.open(link, '_blank');
    } else if (
      actionEnum === TableActions.emailSolid.key ||
      actionEnum === TableActions.whatsappSolid.key
    ) {
      setActiveShareType(actionEnum);
      setActiveItem(item);
      setIsShareDialogOpen(true);
    }
  };

  const debouncedSetFilterData = useDebouncedAction(setFilteredData);

  useEffect(() => {
    getGeneratedProposals();
  }, [getGeneratedProposals]);

  if (!loading && data.totalCount === 0 && firstFetch.current) {
    return <h2 className='text-center'>{t('no_proposals')}</h2>;
  }

  const platformTypeOptions = [
    { label: 'CRM', value: 'CRM' },
    { label: 'MyLeads', value: 'MyLeads' },
  ];
  const onClearedFiltersClick = () => {
    setFilteredData({
      pageIndex: 1,
      pageSize: 25,
      dateFrom: null,
      dateTo: null,
    });
    setState({
      propertyName: '',
      unitRefNumber: '',
      platformType: null,
    });
  };
  return (
    <div className='mx-3 my-2'>
      <div className='w-100 d-flex gap-1 pb-3 fa-center '>
        <div className='d-flex flex-wrap gap-1 w-50'>
          <Inputs
            labelValue={'property_name'}
            value={state?.propertyName}
            onInputChanged={(e) => {
              e.persist();
              debouncedSetFilterData((prev) => ({
                ...prev,
                propertyName: e.target?.value,
              }));
              setState((prev) => ({
                ...prev,
                propertyName: e.target?.value,
              }));
            }}
            parentTranslationPath={GeneratedProposalTranslationPath}
            wrapperClasses='w-25'
          />

          <Inputs
            labelValue={'Unit Ref Num'}
            value={state?.unitRefNumber}
            onInputChanged={(e) => {
              e.persist();
              debouncedSetFilterData((prev) => ({
                ...prev,
                unitRefNumber: e.target?.value,
              }));
              setState((prev) => ({
                ...prev,
                unitRefNumber: e.target?.value,
              }));
            }}
            parentTranslationPath={GeneratedProposalTranslationPath}
            wrapperClasses='w-25'
          />

          <SelectComponet
            data={platformTypeOptions}
            textInput='label'
            valueInput='value'
            labelValue='Platform'
            emptyItem={{
              value: null,
              text: 'Select Platform ',
              isDisabled: false,
            }}
            value={state?.platformType}
            onSelectChanged={(value) => {
              debouncedSetFilterData((prev) => ({
                ...prev,
                platformType: value,
              }));
              setState((prev) => ({
                ...prev,
                platformType: value,
              }));
            }}
            wrapperClasses='w-25'
          />
        </div>
        <div className='w-50 d-flex-column'>
          <DateRangePickerComponent
            ranges={[
              {
                startDate: filteredData.dateFrom,
                endDate: moment(filteredData.dateTo),
                key: 'selection',
              },
            ]}
            onClearClicked={() =>
              setFilteredData((prev) => ({
                ...prev,
                dateFrom: null,
                dateTo: null,
              }))
            }
            onDateChanged={(e) => {
              setFilteredData((filter) => ({
                ...filter,
                dateFrom: new Date(moment(e.selection.startDate).endOf('day')),
                dateTo: new Date(moment(e.selection.endDate).endOf('day')),
              }));
            }}
            wrapperClasses='fas-end w-100'
          />
          <div className='mt-3 fas-end'>
            <ButtonBase
              onClick={onClearedFiltersClick}
              id='onClearedAllFiltersref'
              className='btns theme-solid bg-danger clear-all-btn'
            >
              <span className='mdi mdi-filter-remove m-1' />
              Clear Filters
            </ButtonBase>
          </div>
        </div>
      </div>

      <Tables
        headerData={headerData}
        data={data?.result || []}
        totalItems={data.totalCount}
        onPageIndexChanged={(pageIndex) => {
          setFilteredData((prev) => ({ ...prev, pageIndex: pageIndex + 1 }));
        }}
        onPageSizeChanged={(pageSize) => {
          setFilteredData((prev) => ({ ...prev, pageSize }));
        }}
        itemsPerPage={filteredData.pageSize}
        activePage={filteredData.pageIndex - 1}
        defaultActions={[
          { enum: TableActions.preview.key },
          { enum: TableActions.downloadText.key },
          { enum: TableActions.emailSolid.key },
          { enum: TableActions.whatsappSolid.key },
        ]}
        isLoading={loading}
        actionsOptions={{ onActionClicked }}
        parentTranslationPath={GeneratedProposalTranslationPath}
        focusedRowChanged={(activeRow) => {
          setActiveItem(data?.result[activeRow] || null);
        }}
      />
      <PreviewDialog
        isOpen={isPreviewDialogOpen}
        onClose={() => setIsPreviewDialogOpen(false)}
        proposalFileId={activeItem?.proposalFileId}
        parentTranslationPath={GeneratedProposalTranslationPath}
      />
      <ShareDialog
        open={isShareDialogOpen}
        onClose={() => setIsShareDialogOpen(false)}
        activeItem={activeItem}
        parentTranslationPath={GeneratedProposalTranslationPath}
        actionType={activeShareType}
      />
    </div>
  );
};
export default GeneratedProposalsView;
